// Google Fonts utility
// Note: This uses a demo API key for development only

// Popular Google Fonts list (fallback if API fails)
const POPULAR_FONTS = [
  'Open Sans',
  'Roboto',
  'Lato',
  'Montserrat',
  'Oswald',
  'Source Sans Pro',
  'Raleway',
  'PT Sans',
  'Merriweather',
  'Poppins',
  'Ubuntu',
  'Nunito',
  'Playfair Display',
  'Lora',
  'Mukta',
  'Rubik',
  'Work Sans',
  'Noto Sans',
  'Fira Sans',
  'Quicksand',
  'Karla',
  'Titillium Web',
  'Arimo',
  'Barlow',
  'Cabin',
  'Inter',
  'DM Sans',
  'Manrope',
  'Plus Jakarta Sans',
  'Space Grotesk'
];

// Cache for fetched fonts
let fontsCache = null;
let fontsPromise = null;

/**
 * Clear the fonts cache (useful when API key changes)
 */
export const clearFontsCache = () => {
  fontsCache = null;
  fontsPromise = null;
  console.log('Google Fonts cache cleared');
};

/**
 * Fetch Google Fonts list from API
 * @returns {Promise<Array>} Array of font objects with family name and variants
 */
export const fetchGoogleFonts = async () => {
  // Return cached fonts if available
  if (fontsCache) {
    return fontsCache;
  }

  // Return existing promise if one is in progress
  if (fontsPromise) {
    return fontsPromise;
  }

  // Create new promise
  fontsPromise = (async () => {
    try {
      // Note: Using environment variable or fallback to popular fonts
      // In production, the API key should be stored securely
      const apiKey = process.env.REACT_APP_GOOGLE_FONTS_API_KEY;

      if (!apiKey) {
        console.warn('Google Fonts API key not configured (REACT_APP_GOOGLE_FONTS_API_KEY), using popular fonts list');
        fontsCache = POPULAR_FONTS.map(family => ({ family, variants: ['regular'] }));
        return fontsCache;
      }

      console.log('Fetching Google Fonts with API key...');

      const response = await fetch(
        `https://www.googleapis.com/webfonts/v1/webfonts?sort=popularity&key=${apiKey}`
      );

      if (!response.ok) {
        throw new Error('Failed to fetch Google Fonts');
      }

      const data = await response.json();
      
      // Extract font families and their variants
      fontsCache = data.items.map(font => ({
        family: font.family,
        variants: font.variants || ['regular'],
        category: font.category || 'sans-serif'
      }));

      console.log(`Loaded ${fontsCache.length} Google Fonts`);
      return fontsCache;
    } catch (error) {
      console.error('Error fetching Google Fonts:', error);
      // Fallback to popular fonts
      fontsCache = POPULAR_FONTS.map(family => ({ family, variants: ['regular'] }));
      return fontsCache;
    }
  })();

  return fontsPromise;
};

// Common font weights to load by default
const DEFAULT_WEIGHTS = ['400', '500', '600', '700'];

// Track loaded fonts and their weights
const loadedFonts = new Map();

/**
 * Load a Google Font dynamically
 * @param {string} fontFamily - Font family name
 * @param {string[]} variants - Font variants to load (e.g., ['400', '700'])
 */
export const loadGoogleFont = (fontFamily, variants = DEFAULT_WEIGHTS) => {
  if (!fontFamily || fontFamily === 'inherit' || fontFamily.includes('system') || fontFamily === 'System Default') {
    return;
  }

  const fontId = `google-font-${fontFamily.replace(/\s+/g, '-')}`;
  const weightsKey = variants.sort().join(',');

  // Check if font is already loaded with these weights
  if (loadedFonts.get(fontFamily) === weightsKey) {
    return;
  }

  // Remove existing link if present (to update with new weights)
  const existingLink = document.getElementById(fontId);
  if (existingLink) {
    existingLink.remove();
  }

  // Create link element for Google Fonts
  const link = document.createElement('link');
  link.id = fontId;
  link.rel = 'stylesheet';

  // Build Google Fonts URL with variants
  const family = fontFamily.replace(/\s+/g, '+');
  const weights = variants.map(v => {
    // Convert variant names to weights
    if (v === 'regular') return '400';
    if (v === 'italic') return '400i';
    return v;
  }).join(';');

  link.href = `https://fonts.googleapis.com/css2?family=${family}:wght@${weights}&display=swap`;

  document.head.appendChild(link);
  loadedFonts.set(fontFamily, weightsKey);
};

/**
 * Get font list for display (with optional search filtering)
 * @param {string} searchTerm - Optional search term to filter fonts
 * @returns {Promise<Array>} Filtered array of font families
 */
export const searchGoogleFonts = async (searchTerm = '') => {
  const fonts = await fetchGoogleFonts();
  
  if (!searchTerm) {
    return fonts.map(f => f.family).slice(0, 50); // Return top 50 fonts
  }

  const term = searchTerm.toLowerCase();
  return fonts
    .filter(f => f.family.toLowerCase().includes(term))
    .map(f => f.family)
    .slice(0, 50); // Limit to 50 results
};

/**
 * Get system fonts (common cross-platform fonts)
 * @returns {Array<string>} Array of system font names
 */
export const getSystemFonts = () => {
  return [
    'System Default',
    'Arial',
    'Helvetica',
    'Times New Roman',
    'Georgia',
    'Courier New',
    'Verdana',
    'Trebuchet MS',
    'Comic Sans MS',
    'Impact'
  ];
};

/**
 * Apply font family to element with proper CSS
 * @param {string} fontFamily - Font family name
 * @returns {string} CSS font-family value
 */
export const getFontFamilyCSS = (fontFamily) => {
  if (!fontFamily || fontFamily === 'System Default') {
    return '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif';
  }
  
  // For system fonts, return as-is with fallbacks
  const systemFonts = getSystemFonts();
  if (systemFonts.includes(fontFamily)) {
    return `"${fontFamily}", sans-serif`;
  }
  
  // For Google Fonts, ensure font is loaded
  loadGoogleFont(fontFamily);
  
  return `"${fontFamily}", sans-serif`;
};
